/**
 * Validation and form utilities
 */

import { Countries, Regions } from '../constants'

export interface ValidationRule {
  required?: boolean
  minLength?: number
  maxLength?: number
  pattern?: RegExp
  custom?: (value: any) => boolean | string
}

export interface ValidationResult {
  isValid: boolean
  error?: string
}

/**
 * Get item variants for display
 */
export function getItemVariants(item: any): Array<{ name: string; value: string }> {
  if (!item?.variant?.options) {
    return []
  }

  return item.variant.options
    .filter((option: any) => !option.is_deleted)
    .map((option: any) => ({
      name: option.name,
      value: option.value,
    }))
}

const STATE_REQUIRED_COUNTRIES = ['US', 'CA', 'MX']

/**
 * Update required fields based on country
 */
export function updateRequiredFields(
  requiredFields: string[],
  country: string,
  _isShipping: boolean = false
): string[] {
  const baseFields = [...requiredFields]

  if (STATE_REQUIRED_COUNTRIES.includes(country)) {
    if (!baseFields.includes('state')) {
      baseFields.push('state')
    }
  }

  if (country === 'MX' && !baseFields.includes('company')) {
    baseFields.push('company')
  }

  return baseFields
}

/**
 * Check if field is required for country
 */
export function isFieldRequired(field: string, country: string, isShipping: boolean = false): boolean {
  const prefix = isShipping ? 'ship_' : ''
  const fieldName = field.replace(prefix, '')

  const alwaysRequired = ['first_name', 'last_name', 'address1', 'city', 'zip']
  if (alwaysRequired.includes(fieldName)) {
    return true
  }

  if (fieldName === 'state' && STATE_REQUIRED_COUNTRIES.includes(country)) {
    return true
  }

  if (fieldName === 'company' && country === 'MX') {
    return true
  }

  return false
}

/**
 * Get field label based on country. For Mexico, 'company' becomes 'RFC'.
 */
export function getFieldLabel(
  field: string,
  defaultLabel: string,
  country: string,
  _isShipping: boolean = false
): string {
  const fieldName = field.replace(/^ship_/, '')

  if (fieldName === 'company') {
    return country === 'MX' ? 'RFC' : 'Company'
  }

  if (fieldName === 'zip') {
    if (country === 'CA') {
      return 'Postal Code'
    }
    if (country === 'GB') {
      return 'Postcode'
    }
    return 'ZIP Code'
  }

  if (fieldName === 'state') {
    if (country === 'CA') {
      return 'Province'
    }
    if (country === 'GB') {
      return 'County'
    }
    return 'State'
  }

  return defaultLabel
}

/**
 * Get countries list. When a campaign with allowed_countries is supplied,
 * the list is filtered to that subset. customerCountry is always included
 * so the customer's existing country remains selectable.
 */
export function getCountries(campaign?: any, customerCountry?: string): Array<{ value: string; text: string }> {
  const all = Object.entries(Countries).map(([code, name]) => ({ value: code, text: name }))
  const allowed: string[] | undefined = campaign?.allowed_countries
  if (!allowed || !allowed.length) {
    return all
  }

  const filtered = all.filter(c => allowed.includes(c.value))
  if (customerCountry && !filtered.find(c => c.value === customerCountry)) {
    const match = all.find(c => c.value === customerCountry)
    if (match) {
      filtered.push(match)
    }
  }
  return filtered
}

/**
 * Check if country uses US or CA format
 */
export function isCountryUSOrCA(country: string): boolean {
  return country === 'US' || country === 'CA'
}

/**
 * Get states/provinces for country. When a campaign is supplied, entries
 * listed in campaign.blacklisted_states[country] are filtered out.
 */
export function getCountryStates(campaignOrCountry: any, countryArg?: string): Array<{ value: string; text: string }> {
  let campaign: any = null
  let country: string
  if (typeof campaignOrCountry === 'string') {
    country = campaignOrCountry
  } else {
    campaign = campaignOrCountry
    country = countryArg || 'US'
  }

  const states = [...(Regions[country] || [])]
  const blacklist = campaign?.blacklisted_states?.[country]
  if (Array.isArray(blacklist) && blacklist.length) {
    return states.filter(s => !blacklist.includes(s.value))
  }
  return states
}

/**
 * Get states for campaign (with fallback)
 */
export function getStates(campaign: any, country: string = 'US'): Array<{ value: string; text: string }> {
  if (campaign?.states?.[country]) {
    return campaign.states[country]
  }

  return getCountryStates(campaign, country)
}

/**
 * Format zip/postal code field. US: 5 digits, or 5+4 with hyphen. CA: A1A 1A1.
 */
export function formatZipField(value: string, country: string): string {
  if (!value) {
    return value
  }

  if (country === 'US') {
    const digits = value.replace(/\D/g, '').slice(0, 9)
    if (digits.length > 5) {
      return `${digits.slice(0, 5)}-${digits.slice(5)}`
    }
    return digits
  }

  if (country === 'CA') {
    const cleaned = value.replace(/\s+/g, '').toUpperCase().slice(0, 6)
    if (cleaned.length > 3) {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`
    }
    return cleaned
  }

  return value
}

/**
 * Validate email address
 */
export function validateEmail(email: string): ValidationResult {
  if (!email) {
    return { isValid: false, error: 'Email is required' }
  }

  // More strict email validation to reject consecutive dots but allow + character
  const emailRegex = /^[a-zA-Z0-9]([a-zA-Z0-9._+-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9.-]*[a-zA-Z0-9])?\.[a-zA-Z]{2,}$/
  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Please enter a valid email address' }
  }

  // Additional check for consecutive dots
  if (email.includes('..')) {
    return { isValid: false, error: 'Please enter a valid email address' }
  }

  return { isValid: true }
}

/**
 * Validate phone number
 */
export function validatePhone(phone: string): ValidationResult {
  if (!phone) {
    return { isValid: false, error: 'Phone number is required' }
  }

  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length < 10) {
    return { isValid: false, error: 'Phone number must be at least 10 digits' }
  }

  return { isValid: true }
}

/**
 * Validate credit card number (basic Luhn check)
 */
export function validateCreditCard(cardNumber: string): ValidationResult {
  if (!cardNumber) {
    return { isValid: false, error: 'Card number is required' }
  }

  const cleaned = cardNumber.replace(/[\s-]/g, '')

  // Basic length check
  if (cleaned.length < 13 || cleaned.length > 19) {
    return { isValid: false, error: 'Invalid card number length' }
  }

  // Luhn algorithm check
  let sum = 0
  let isEven = false

  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = parseInt(cleaned[i])

    if (isEven) {
      digit *= 2
      if (digit > 9) {
        digit = (digit % 10) + 1
      }
    }

    sum += digit
    isEven = !isEven
  }

  if (sum % 10 !== 0) {
    return { isValid: false, error: 'Invalid card number' }
  }

  return { isValid: true }
}

/**
 * Validate CVV
 */
export function validateCVV(cvv: string, cardType?: string): ValidationResult {
  if (!cvv) {
    return { isValid: false, error: 'CVV is required' }
  }

  const expectedLength = cardType === 'amex' ? 4 : 3

  if (cvv.length !== expectedLength) {
    return { isValid: false, error: `CVV must be ${expectedLength} digits` }
  }

  return { isValid: true }
}

/**
 * Generic field validation
 */
export function validateField(value: any, rules: ValidationRule): ValidationResult {
  if (rules.required && (!value || (typeof value === 'string' && !value.trim()))) {
    return { isValid: false, error: 'This field is required' }
  }

  if (value && typeof value === 'string') {
    if (rules.minLength && value.length < rules.minLength) {
      return { isValid: false, error: `Minimum length is ${rules.minLength}` }
    }

    if (rules.maxLength && value.length > rules.maxLength) {
      return { isValid: false, error: `Maximum length is ${rules.maxLength}` }
    }

    if (rules.pattern && !rules.pattern.test(value)) {
      return { isValid: false, error: 'Invalid format' }
    }
  }

  if (rules.custom) {
    const customResult = rules.custom(value)
    if (typeof customResult === 'string') {
      return { isValid: false, error: customResult }
    }
    if (!customResult) {
      return { isValid: false, error: 'Invalid value' }
    }
  }

  return { isValid: true }
}

// Additional validation functions for backwards compatibility

/**
 * Simple email validation that returns boolean
 */
export function validateEmailSimple(email: string): boolean {
  const result = validateEmail(email)
  return result.isValid
}

/**
 * Simple credit card validation that returns boolean
 */
export function validateCreditCardSimple(cardNumber: string): boolean {
  const result = validateCreditCard(cardNumber)
  return result.isValid
}

/**
 * Validate credit card CVC
 */
export function validateCreditCardCVC(cvc: string, cardType: string): boolean {
  if (!cvc || !cvc.trim()) {
    return false
  }

  const cleanCVC = cvc.replace(/\D/g, '')

  if (cardType.toLowerCase() === 'amex') {
    return cleanCVC.length === 4
  } else {
    return cleanCVC.length === 3
  }
}

/**
 * Validate credit card expiry
 */
export function validateCreditCardExpiry(month: string, year: string): boolean {
  if (!month || !year) {
    return false
  }

  const monthNum = parseInt(month, 10)
  const yearNum = parseInt(year, 10)

  if (monthNum < 1 || monthNum > 12) {
    return false
  }
  if (yearNum < 1000) {
    return false
  } // Require 4-digit year

  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1

  if (yearNum < currentYear) {
    return false
  }
  if (yearNum === currentYear && monthNum < currentMonth) {
    return false
  }

  return true
}

/**
 * Check if field is valid for country
 */
export function isValidCountryForField(country: string, field: string): boolean {
  const requiredFields = getRequiredFields(country)
  return requiredFields.includes(field)
}

/**
 * Get required fields for country
 */
export function getRequiredFields(country: string): string[] {
  const fields: string[] = ['first_name', 'last_name', 'address', 'city', 'email']

  if (country === 'US') {
    fields.push('state', 'zip')
  } else if (country === 'CA') {
    fields.push('postal_code')
  }

  return fields
}

/**
 * Get country-specific mask for field
 */
export function getCountrySpecificMask(country: string, field: string): string | undefined {
  if (country === 'US' && field === 'zip') {
    return '99999'
  } else if (country === 'CA' && field === 'postal_code') {
    return 'A9A 9A9'
  }

  return undefined
}
